import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '../../../../components/Layout';
import {
  salesReportService,
  SalesByCustomerDTO,
  TopCustomerDTO,
} from '../../../../services/salesReportService';
import {
  Users, DollarSign, CheckCircle2, AlertTriangle,
  Receipt, TrendingUp, Trophy, Search, ArrowLeft,
} from 'lucide-react';
import './SalesReportCommon.css';

// ── Helpers ───────────────────────────────────────────────────────────────────

const today    = () => new Date().toISOString().split('T')[0];
const monthAgo = () => {
  const d = new Date();
  d.setMonth(d.getMonth() - 1);
  return d.toISOString().split('T')[0];
};

const fmtDate = (iso: string) => {
  try { return new Date(iso).toLocaleDateString(); } catch { return iso; }
};

// ── Component ─────────────────────────────────────────────────────────────────

const SalesByCustomerReportPage: React.FC = () => {
  const { t }    = useTranslation();
  const navigate = useNavigate();

  const fmt = (n: number) =>
    new Intl.NumberFormat(undefined, {
      style: 'decimal', minimumFractionDigits: 2, maximumFractionDigits: 2,
    }).format(n);

  // ── Filter state
  const [filters, setFilters] = useState({
    startDate: monthAgo(),
    endDate:   today(),
  });

  type SortKey = 'revenue' | 'invoices';
  const [sortBy, setSortBy] = useState<SortKey>('revenue');

  // ── Result state
  const [hasSearched,  setHasSearched]  = useState(false);
  const [searching,    setSearching]    = useState(false);
  const [customers,    setCustomers]    = useState<SalesByCustomerDTO[]>([]);
  const [topCustomers, setTopCustomers] = useState<TopCustomerDTO[]>([]);

  // ── Derived: sorted customer list
  const sortedCustomers = [...customers].sort((a, b) =>
    sortBy === 'invoices'
      ? b.numberOfInvoices - a.numberOfInvoices
      : b.totalSales       - a.totalSales
  );

  // ── Computed totals
  const totals = customers.reduce(
    (acc, c) => ({
      revenue:          acc.revenue          + c.totalSales,
      paid:             acc.paid             + c.amountPaid,
      outstanding:      acc.outstanding      + c.amountOutstanding,
      invoices:         acc.invoices         + c.numberOfInvoices,
      restaurantOrders: acc.restaurantOrders + c.numberOfRestaurantOrders,
    }),
    { revenue: 0, paid: 0, outstanding: 0, invoices: 0, restaurantOrders: 0 }
  );

  const combinedTransactions = totals.invoices + totals.restaurantOrders;
  const avgInvoiceValue = combinedTransactions > 0
    ? totals.revenue / combinedTransactions
    : 0;

  // ── Handlers
  const handleSearch = async () => {
    setSearching(true);
    setHasSearched(true);
    try {
      const [custRes, topRes] = await Promise.allSettled([
        salesReportService.getSalesByCustomer(filters),
        salesReportService.getTopCustomers({ ...filters, limit: 5 }),
      ]);

      if (custRes.status === 'fulfilled') {
        setCustomers((custRes.value.data as any)?.data ?? custRes.value.data ?? []);
      }
      if (topRes.status === 'fulfilled') {
        setTopCustomers((topRes.value.data as any)?.data ?? topRes.value.data ?? []);
      }
    } catch {
      setCustomers([]); setTopCustomers([]);
    } finally {
      setSearching(false);
    }
  };

  const handleFilterChange = (field: string, value: string) =>
    setFilters(prev => ({ ...prev, [field]: value }));

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <Layout>
      <div className="ssr-page">

        {/* ── Hero ──────────────────────────────────────────────────── */}
        <div className="ssr-hero">
          <div>
            <h1 className="ssr-hero-title">
              <Users size={22} strokeWidth={1.8} />
              {t('salesReports.byCustomerTitle', 'Sales by Customer')}
            </h1>
            <p className="ssr-hero-sub">
              {t('salesReports.byCustomerSubtitle', 'Customer revenue, invoice counts and outstanding balances within a date range')}
            </p>
          </div>

          <div className="ssr-hero-actions">
            <button className="ssr-back-btn" onClick={() => navigate('/reports/sales')}>
              <ArrowLeft size={14} strokeWidth={2} />
              {t('common.back', 'Back')}
            </button>
          </div>
        </div>

        {/* ── Filter card ───────────────────────────────────────────── */}
        <div className="ims-card">
          <div className="ims-card-header">
            <span className="ims-card-title">
              <Search size={14} strokeWidth={2} style={{ verticalAlign: 'middle', marginInlineEnd: 6 }} />
              {t('salesReports.dateRangeFilter', 'Date Range Filter')}
            </span>
          </div>
          <div className="ims-card-body" style={{ padding: '1rem 1.25rem' }}>
            <div className="ssr-filter-bar">

              <div className="ssr-filter-group">
                <span className="ssr-filter-label">{t('common.startDate', 'From Date')}</span>
                <input
                  type="date"
                  className="ims-form-control"
                  value={filters.startDate}
                  onChange={e => handleFilterChange('startDate', e.target.value)}
                />
              </div>

              <div className="ssr-filter-group">
                <span className="ssr-filter-label">{t('common.endDate', 'To Date')}</span>
                <input
                  type="date"
                  className="ims-form-control"
                  value={filters.endDate}
                  onChange={e => handleFilterChange('endDate', e.target.value)}
                />
              </div>

              <div className="ssr-filter-group" style={{ maxWidth: 170 }}>
                <span className="ssr-filter-label">{t('common.sortBy', 'Sort by')}</span>
                <select
                  className="ims-form-control"
                  value={sortBy}
                  onChange={e => setSortBy(e.target.value as SortKey)}
                >
                  <option value="revenue">{t('salesReports.byRevenue', 'Revenue (high → low)')}</option>
                  <option value="invoices">{t('salesReports.byInvoices', 'Invoice Count')}</option>
                </select>
              </div>

              <button
                className="ims-btn ims-btn-primary"
                onClick={handleSearch}
                disabled={searching}
                style={{ alignSelf: 'flex-end' }}
              >
                {searching ? (
                  <><span className="ssr-spinner" /> {t('common.searching', 'Searching…')}</>
                ) : (
                  <><Search size={14} strokeWidth={2.5} /> {t('common.search', 'Search')}</>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* ── Initial prompt ────────────────────────────────────────── */}
        {!hasSearched && (
          <div className="ssr-prompt">
            <Users size={48} strokeWidth={1} />
            <p>
              {t('salesReports.searchPrompt', 'Set a date range and click')}{' '}
              <strong>{t('common.search', 'Search')}</strong>{' '}
              {t('salesReports.searchPromptCustomerEnd', 'to load customer sales.')}
            </p>
          </div>
        )}

        {/* ── Results ───────────────────────────────────────────────── */}
        {hasSearched && !searching && (
          <>
            {/* ── KPI row ──────────────────────────────────────────── */}
            {customers.length > 0 && (
              <div className="ims-stats-row" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))' }}>

                <div className="ims-stat-card">
                  <div className="ims-stat-icon ssr-icon-total"><Users size={20} strokeWidth={1.6} /></div>
                  <div className="ims-stat-info">
                    <span className="ims-stat-value">{customers.length}</span>
                    <span className="ims-stat-label">{t('salesReports.totalCustomers', 'Total Customers')}</span>
                  </div>
                </div>

                <div className="ims-stat-card">
                  <div className="ims-stat-icon ssr-icon-sales"><DollarSign size={20} strokeWidth={1.6} /></div>
                  <div className="ims-stat-info">
                    <span className="ims-stat-value" style={{ fontSize: '1.05rem' }}>{fmt(totals.revenue)}</span>
                    <span className="ims-stat-label">{t('salesReports.totalRevenue', 'Total Revenue')}</span>
                  </div>
                </div>

                <div className="ims-stat-card">
                  <div className="ims-stat-icon ssr-icon-paid"><CheckCircle2 size={20} strokeWidth={1.6} /></div>
                  <div className="ims-stat-info">
                    <span className="ims-stat-value" style={{ fontSize: '1.05rem' }}>{fmt(totals.paid)}</span>
                    <span className="ims-stat-label">{t('salesReports.amountPaid', 'Amount Paid')}</span>
                  </div>
                </div>

                <div className="ims-stat-card">
                  <div className="ims-stat-icon ssr-icon-unpaid"><AlertTriangle size={20} strokeWidth={1.6} /></div>
                  <div className="ims-stat-info">
                    <span className="ims-stat-value" style={{ fontSize: '1.05rem' }}>{fmt(totals.outstanding)}</span>
                    <span className="ims-stat-label">{t('salesReports.outstanding', 'Outstanding')}</span>
                  </div>
                </div>

                <div className="ims-stat-card">
                  <div className="ims-stat-icon ssr-icon-rate"><Receipt size={20} strokeWidth={1.6} /></div>
                  <div className="ims-stat-info">
                    <span className="ims-stat-value">{totals.invoices}</span>
                    <span className="ims-stat-label">{t('salesReports.totalInvoices', 'Total Invoices')}</span>
                  </div>
                </div>

                <div className="ims-stat-card">
                  <div className="ims-stat-icon ssr-icon-avg"><TrendingUp size={20} strokeWidth={1.6} /></div>
                  <div className="ims-stat-info">
                    <span className="ims-stat-value" style={{ fontSize: '1.05rem' }}>{fmt(avgInvoiceValue)}</span>
                    <span className="ims-stat-label">{t('salesReports.avgInvoice', 'Avg. Invoice Value')}</span>
                  </div>
                </div>
              </div>
            )}

            {/* ── Customer table ────────────────────────────────────── */}
            {customers.length === 0 ? (
              <div className="ims-card">
                <div className="ssr-empty">
                  {t('salesReports.noCustomers', 'No customer sales found for the selected period.')}
                </div>
              </div>
            ) : (
              <div className="ims-card">
                <div className="ims-card-header">
                  <span className="ims-card-title">
                    <Users size={14} strokeWidth={2} style={{ verticalAlign: 'middle', marginInlineEnd: 6 }} />
                    {t('salesReports.allCustomers', 'All Customers')}
                    <span style={{ fontWeight: 400, fontSize: 12, color: 'var(--ims-text-muted)', marginInlineStart: 6 }}>
                      ({customers.length} {customers.length !== 1
                        ? t('salesReports.records', 'records')
                        : t('salesReports.record', 'record')})
                    </span>
                  </span>
                </div>
                <div className="ims-card-body" style={{ padding: 0, overflowX: 'auto' }}>
                  <table className="ssr-table">
                    <thead>
                      <tr>
                        <th style={{ width: 36 }}>#</th>
                        <th>{t('common.customer', 'Customer')}</th>
                        <th>{t('salesReports.customerCode', 'Code')}</th>
                        <th className="num">{t('salesReports.revenue', 'Revenue')}</th>
                        <th className="num">{t('salesReports.amountPaid', 'Paid')}</th>
                        <th className="num">{t('salesReports.outstanding', 'Outstanding')}</th>
                        <th className="num">{t('salesReports.invoices', 'Invoices')}</th>
                        <th className="num">{t('salesReports.avgOrderValue', 'Avg. Invoice')}</th>
                        <th>{t('salesReports.lastSale', 'Last Sale')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedCustomers.map((c, idx) => (
                        <tr key={c.customerId}>
                          <td className="ssr-row-num">{idx + 1}</td>
                          <td style={{ fontWeight: 600 }}>{c.customerName}</td>
                          <td><span className="ssr-sku">{c.customerCode}</span></td>
                          <td className="num ssr-revenue-value">{fmt(c.totalSales)}</td>
                          <td className="num ssr-balance-zero">{fmt(c.amountPaid)}</td>
                          <td className={`num ${c.amountOutstanding > 0 ? 'ssr-balance-due' : 'ssr-balance-zero'}`}>
                            {fmt(c.amountOutstanding)}
                          </td>
                          <td className="num">
                            {c.numberOfInvoices}
                            {c.numberOfRestaurantOrders > 0 && (
                              <span style={{ display: 'block', fontSize: 11, color: 'var(--ims-text-muted)' }}>
                                +{c.numberOfRestaurantOrders} {t('salesReports.restaurantOrdersLower', 'restaurant')}
                              </span>
                            )}
                          </td>
                          <td className="num">{fmt(c.averageInvoiceValue)}</td>
                          <td>{c.lastSaleDate ? fmtDate(c.lastSaleDate) : '—'}</td>
                        </tr>
                      ))}
                    </tbody>

                    {/* Grand total footer */}
                    <tfoot>
                      <tr>
                        <td></td>
                        <td colSpan={2}>
                          {t('salesReports.total', 'TOTAL')} ({customers.length}{' '}
                          {t('salesReports.customersLower', 'customers')})
                        </td>
                        <td className="num">{fmt(totals.revenue)}</td>
                        <td className="num">{fmt(totals.paid)}</td>
                        <td className="num">{fmt(totals.outstanding)}</td>
                        <td className="num">
                          {totals.invoices}
                          {totals.restaurantOrders > 0 && (
                            <span style={{ display: 'block', fontSize: 11, fontWeight: 400, color: 'var(--ims-text-muted)' }}>
                              +{totals.restaurantOrders} {t('salesReports.restaurantOrdersLower', 'restaurant')}
                            </span>
                          )}
                        </td>
                        <td className="num">{fmt(avgInvoiceValue)}</td>
                        <td></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            )}

            {/* ── Top 5 Customers breakdown ────────────────────────── */}
            {topCustomers.length > 0 && (
              <>
                <span className="ssr-section-title">
                  {t('salesReports.topCustomers', 'Top Customers')}
                </span>
                <div className="ssr-breakdown-grid">
                  {topCustomers.map((c, idx) => (
                    <div key={c.customerId} className="ssr-breakdown-card ssr-breakdown-rank">
                      <div className="ssr-breakdown-label">
                        <Trophy size={11} strokeWidth={2} style={{ verticalAlign: 'middle', marginInlineEnd: 3 }} />
                        #{idx + 1}
                      </div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--ims-text-main)', marginBottom: 4 }}>
                        {c.customerName}
                      </div>
                      <div className="ssr-breakdown-value" style={{ fontSize: '1.5rem' }}>
                        {fmt(c.totalSales)}
                      </div>
                      <div className="ssr-breakdown-pct">
                        {c.numberOfInvoices}{' '}
                        {c.numberOfInvoices !== 1
                          ? t('salesReports.invoicesLower', 'invoices')
                          : t('salesReports.invoiceLower', 'invoice')}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </>
        )}

      </div>
    </Layout>
  );
};

export default SalesByCustomerReportPage;
